import React, { useState } from 'react';
import { vendorsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, Loader2, CheckCircle2, AlertTriangle, RefreshCcw, PlusCircle, FileWarning, Lock } from 'lucide-react';
import { useCap } from '../components/auth/RequireCap';

/**
 * WinTeam VMF import — two-phase (preview → confirm).
 *
 * Preview parses the workbook, matches each row against existing vendors by
 * normalized name, and returns three buckets:
 *   • will_update    — existing vendor matched (id + code preserved)
 *   • will_create    — no match; new auto-generated code
 *   • ambiguous      — near-hit; NOT auto-merged, flagged for review
 *
 * Confirm applies only the update+create buckets. Ambiguous rows are left
 * alone so a human can rename/edit and re-run the import.
 */
export default function VendorImportTab() {
  const canImport = useCap('purchase_orders', 'add');
  const [file, setFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f && f.name.toLowerCase().endsWith('.xlsx')) {
      setFile(f);
      setPreview(null);
      setCommitted(null);
    } else {
      toast.error('Please upload an .xlsx workbook');
    }
  };

  const runPreview = async () => {
    if (!file) return;
    setPreviewLoading(true);
    setPreview(null);
    setCommitted(null);
    setErrorMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await vendorsAPI.importPreview(fd);
      setPreview(data);
      toast.success(`Preview ready: ${data.totals.will_update} updates, ${data.totals.will_create} creates, ${data.totals.ambiguous} needs review`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.userMessage || 'Preview failed';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const runConfirm = async () => {
    if (!preview?.preview_id) return;
    if (!window.confirm(
      `Apply this import? ${preview.totals.will_update} existing vendor(s) will be updated and ${preview.totals.will_create} new vendor(s) will be created.\n\n` +
      `${preview.totals.ambiguous} ambiguous row(s) will NOT be written (they must be resolved manually).\n\n` +
      `Continue?`
    )) return;
    setConfirmLoading(true);
    try {
      const { data } = await vendorsAPI.importConfirm(preview.preview_id);
      setCommitted(data);
      setPreview(null);
      setFile(null);
      toast.success(`Import complete: ${data.updated_count} updated, ${data.created_count} created`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.userMessage || 'Import failed';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setConfirmLoading(false);
    }
  };

  if (!canImport) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 p-6 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/60" data-testid="vendor-import-denied">
            <Lock className="w-6 h-6 text-slate-400" />
            <div>
              <p className="font-medium text-slate-700">Import not available for your role</p>
              <p className="text-sm text-slate-500">Vendor import requires the <code className="px-1 py-0.5 bg-slate-200 rounded text-xs">purchase_orders:add</code> capability.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendor-import-tab">
      <Card>
        <CardHeader>
          <CardTitle>WinTeam Vendor Master File Import</CardTitle>
          <CardDescription>
            Upload the WinTeam VMF (.xlsx). Only rows where <code className="bg-slate-100 rounded px-1">ActiveYN = Yes</code>
            &nbsp;are considered. Match happens by normalized vendor name. Existing vendors are updated
            in place (ID and code preserved, PO relationships intact); unmatched rows become new vendors with an
            auto-generated code. Nothing is written until you confirm.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <label className="flex-1 cursor-pointer" data-testid="vendor-file-label">
              <div className={`flex items-center justify-center gap-3 p-6 border-2 border-dashed rounded-xl transition-colors ${file ? 'border-brand-wordmark bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <Upload className="w-6 h-6 text-slate-400" />
                <div className="text-center">
                  {file ? (
                    <p className="font-medium text-brand-wordmark">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>
                  ) : (
                    <>
                      <p className="font-medium text-slate-700">Click to select .xlsx workbook</p>
                      <p className="text-sm text-slate-500">2026-06-24 Active VMF.xlsx or similar</p>
                    </>
                  )}
                </div>
              </div>
              <input type="file" accept=".xlsx" onChange={handleFileChange} className="hidden" data-testid="vendor-file-input" />
            </label>
            <Button
              onClick={runPreview}
              disabled={!file || previewLoading}
              className="btn-navy h-12 px-6"
              data-testid="vendor-preview-btn"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
              {previewLoading ? 'Parsing...' : 'Run Dry-Run Preview'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {errorMsg && (
        <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="vendor-import-error">
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Dry-run results */}
      {preview && (
        <div className="space-y-4" data-testid="vendor-import-preview">
          <Card>
            <CardHeader>
              <CardTitle>Dry-Run Preview</CardTitle>
              <CardDescription>Review the three buckets below. Nothing has been written yet.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{preview.totals.parsed}</p>
                  <p className="text-sm text-slate-500">Parsed (Active)</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{preview.totals.will_update}</p>
                  <p className="text-sm text-slate-500">Will Update</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-600">{preview.totals.will_create}</p>
                  <p className="text-sm text-slate-500">Will Create</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{preview.totals.ambiguous}</p>
                  <p className="text-sm text-slate-500">Ambiguous</p>
                </div>
              </div>
              <Button
                onClick={runConfirm}
                disabled={confirmLoading || (preview.totals.will_update + preview.totals.will_create) === 0}
                className="btn-navy w-full h-12"
                data-testid="vendor-confirm-btn"
              >
                {confirmLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {confirmLoading ? 'Writing...' : `Confirm & Import (${preview.totals.will_update + preview.totals.will_create} vendors)`}
              </Button>
              {preview.totals.ambiguous > 0 && (
                <p className="text-xs text-amber-700 mt-2 text-center">
                  ⚠️ {preview.totals.ambiguous} ambiguous row(s) will NOT be written — resolve them by hand after import.
                </p>
              )}
            </CardContent>
          </Card>

          {preview.will_update.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <RefreshCcw className="w-5 h-5" /> Will Update ({preview.will_update.length})
                </CardTitle>
                <CardDescription>These vendors already exist and will be updated in place (id and code preserved).</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Existing Name</TableHead>
                      <TableHead>Existing Code</TableHead>
                      <TableHead>WinTeam #</TableHead>
                      <TableHead>New City / State</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.will_update.slice(0, 100).map((row, i) => (
                      <TableRow key={i} data-testid={`vendor-update-row-${i}`}>
                        <TableCell className="font-medium">{row.existing_name}</TableCell>
                        <TableCell className="font-mono text-xs">{row.existing_code}</TableCell>
                        <TableCell className="font-mono text-xs">{row.new_data.winteam_vendor_number || '—'}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {[row.new_data.city, row.new_data.state].filter(Boolean).join(', ') || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {preview.will_update.length > 100 && (
                  <p className="text-xs text-slate-500 mt-2">…and {preview.will_update.length - 100} more</p>
                )}
              </CardContent>
            </Card>
          )}

          {preview.will_create.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700">
                  <PlusCircle className="w-5 h-5" /> Will Create ({preview.will_create.length})
                </CardTitle>
                <CardDescription>New vendors will be created with an auto-generated code.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>WinTeam #</TableHead>
                      <TableHead>City / State</TableHead>
                      <TableHead>Payment Method</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.will_create.slice(0, 100).map((row, i) => (
                      <TableRow key={i} data-testid={`vendor-create-row-${i}`}>
                        <TableCell className="font-medium">{row.new_data.name}</TableCell>
                        <TableCell className="font-mono text-xs">{row.new_data.winteam_vendor_number || '—'}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {[row.new_data.city, row.new_data.state].filter(Boolean).join(', ') || '—'}
                        </TableCell>
                        <TableCell className="text-xs">{row.new_data.default_payment_method || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {preview.will_create.length > 100 && (
                  <p className="text-xs text-slate-500 mt-2">…and {preview.will_create.length - 100} more</p>
                )}
              </CardContent>
            </Card>
          )}

          {preview.ambiguous.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" /> Ambiguous — Needs Review ({preview.ambiguous.length})
                </CardTitle>
                <CardDescription>Names partially match existing vendors. These rows will be SKIPPED. Rename in the source or edit vendors manually, then re-run.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Import Name</TableHead>
                      <TableHead>Possible Matches</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.ambiguous.map((row, i) => (
                      <TableRow key={i} data-testid={`vendor-ambiguous-row-${i}`}>
                        <TableCell className="font-medium">{row.import_name}</TableCell>
                        <TableCell className="text-sm">
                          {row.candidates.map(c => (
                            <div key={c.id} className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{c.code}</Badge>
                              <span>{c.name}</span>
                            </div>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {preview.skipped.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-700 text-base">
                  <FileWarning className="w-5 h-5" /> Skipped ({preview.skipped.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm space-y-1">
                  {preview.skipped.map((s, i) => (
                    <li key={i} className="text-slate-600">
                      <span className="font-medium">{s.reason}</span>
                      {s.names && <span className="text-xs text-slate-500"> — {s.names.slice(0, 3).join(', ')}{s.names.length > 3 && '…'}</span>}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Post-commit summary */}
      {committed && (
        <Card className="border-emerald-200" data-testid="vendor-import-result">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" /> Import Complete
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-blue-600">{committed.updated_count}</p>
                <p className="text-sm text-slate-500">Updated</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-emerald-600">{committed.created_count}</p>
                <p className="text-sm text-slate-500">Created</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
